import { Divider, List, ListItem, ListItemText, Typography, useMediaQuery, useTheme } from "@mui/material";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Grid from '@mui/material/Grid2';
import Link from '@mui/material/Link';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '../../components/CustomButton';
import CustomCard from '../../components/CustomCard';
import CustomTextField from '../../components/CustomTextField';
import CustomTypography from "../../components/CustomTypography";
import { triggerGTMEvent } from "../../components/GTMEvent";
import { CurrencyFormatterUtils } from '../currency-formatter/currency-formatter-utils';
import * as Data from './sipinvestment-calculator-data';
import { setExpectedReturnRate, setSipAmount, setTenureInYears } from './sipinvestment-calculator-reducer'; // Path to your reducer
import * as Utils from './sipinvestment-calculator-utils';

const Example = ({ router }) => {
    const dispatch = useDispatch();
    const sipAmount = useSelector((state) => state.sipInvestmentCalculator.sipAmount);
    const tenureInYears = useSelector((state) => state.sipInvestmentCalculator.tenureInYears);
    const expectedReturnRate = useSelector((state) => state.sipInvestmentCalculator.expectedReturnRate);
    const currencyFormatter = useSelector((state) => state.currencyDataFormatter.formatter || {});

    const [report, setReport] = useState(Utils.generateReport(sipAmount, tenureInYears, expectedReturnRate));

    const scrollToTopRef = useRef(null);
    useEffect(() => {
        scrollToTopRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const navigateTo = (path) => {
        router.navigate(path); // This navigates to the specific page
    };

    const handleSipAmount = (event) => {
        dispatch(setSipAmount(event.target.value));
        setReport(null);
    }

    const handleTenureInYears = (event) => {
        dispatch(setTenureInYears(event.target.value));
        setReport(null);
    }

    const handleExpectedReturnRate = (event) => {
        dispatch(setExpectedReturnRate(event.target.value));
        setReport(null);
    }

    const generateReport = () => {
        triggerGTMEvent("Generate Report", "SIP > Generate Report");
        const report = Utils.generateReport(sipAmount, tenureInYears, expectedReturnRate);
        setReport(report);
    };

    return (
        <div ref={scrollToTopRef}>

            {/* BREADCRUMPS WIDGET START */}
            <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }} sx={{ margin: '10px' }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" onClick={() => navigateTo('/')}>
                            Home
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>SIP Investment Calculator</Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>
            {/* BREADCRUMPS WIDGET ENDS */}

            {/* INPUT WIDGET START */}
            <Grid container spacing={1}>
                <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                    <CustomCard
                        title="SIP Investment"
                    >
                        <CustomTextField
                            label="Monthly SIP Amount"
                            value={sipAmount}
                            type="number"
                            onChange={handleSipAmount}
                        />
                        <CustomTextField
                            label="Tenure (In Years)"
                            value={tenureInYears}
                            type="number"
                            onChange={handleTenureInYears}
                        />
                        <CustomTextField
                            label="Expected Return Rate"
                            value={expectedReturnRate}
                            type="number"
                            onChange={handleExpectedReturnRate}
                        />
                        <CustomButton
                            variant="contained"
                            color="success"
                            onClick={generateReport}
                            fullWidth={true}
                        >
                            Calculate
                        </CustomButton>
                    </CustomCard>
                </Grid>
                <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
                    {report &&
                        <div>
                            <CustomCard
                                title="Profit"
                                description={
                                    <>Your investment of <strong>{CurrencyFormatterUtils(Data.getInvestedAmount(report), currencyFormatter)}</strong> will earn you a profit of</>
                                }
                            >
                                <CustomTypography variant="h6" color="success" fontWeight="bold">
                                    {CurrencyFormatterUtils(Data.getProfitAmount(report), currencyFormatter)}   ({Data.getProfitPercentage(report)}%)
                                </CustomTypography>
                            </CustomCard>
                            <CustomCard
                                title="Corpus"
                                description="You'll receive a corpus at the end of the tenure, amounting to"
                            >
                                <CustomTypography variant="h6" color="success" fontWeight="bold">
                                    {CurrencyFormatterUtils(Data.getCorpus(report), currencyFormatter)}
                                </CustomTypography>
                            </CustomCard>
                        </div>
                    }
                </Grid>
            </Grid>
            {/* INPUT WIDGET END */}

            {/* {
                report && !isMobile &&
                <Grid container >
                    <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                        <CustomCard title="Investment vs Profit vs Corpus">
                            <LineChart
                                dataset={report.reportOfMultipleYears}
                                xAxis={Data.graphXAxis}
                                series={Data.graphSeries}
                                width={1050}
                                height={400}
                                margin={{ left: 100, right: 100 }}
                            />
                        </CustomCard>
                    </Grid>
                </Grid>
            } */}
            {/* REPORT WIDGET ENDS */}

            {/* AD WIDGET START */}
            {/* <Grid container spacing={1} >
                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                    <AdsComponent dataAdSlot='3779271985' />
                </Grid>
            </Grid> */}
            {/* AD WIDGET ENDS */}

            {/* BLOG WIDGET START */}
            <CustomCard>
                <Typography variant="h5" gutterBottom>
                    Why Use Our SIP Investment Calculator? Understanding the Benefits
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body1" paragraph>
                    Consistent investing through SIPs is one of the most effective ways to build wealth over time. Our SIP Investment
                    Calculator helps you estimate the future value of your monthly investments, ensuring you stay on track with your
                    financial goals. Here’s why this tool is beneficial:
                </Typography>

                <List>
                    <ListItem>
                        <ListItemText
                            primary="1. Accurate Future Value Estimation"
                            secondary="By entering your monthly investment, expected return rate, and investment duration, you can instantly calculate how much wealth you’ll accumulate over time."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="2. Power of Compounding"
                            secondary="The calculator shows how your regular investments grow exponentially due to compounding returns, highlighting the benefits of long-term investing."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="3. Goal-Oriented Planning"
                            secondary="Whether you’re saving for retirement, education, or a major purchase, the calculator helps you understand if your SIPs are sufficient to meet your goals."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="4. Flexibility to Compare Scenarios"
                            secondary="Easily adjust the investment amount, return rate, or tenure to compare different strategies and find the most effective plan to grow your savings."
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemText
                            primary="5. Better Budget Management"
                            secondary="Knowing your monthly investment commitment in advance helps you manage your budget effectively while still working towards long-term financial security."
                        />
                    </ListItem>
                </List>

                <Typography variant="h5" gutterBottom>
                    How to Use the SIP Investment Calculator Effectively?
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <List>
                    <ListItem>
                        <ListItemText primary="Enter SIP Details" secondary="Provide your monthly SIP amount, expected return rate, and investment duration." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Review Future Value" secondary="Understand how much your regular investments will grow over time." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Experiment with Different Scenarios" secondary="Adjust the investment amount or tenure to see how small changes can lead to larger savings." />
                    </ListItem>
                    <ListItem>
                        <ListItemText primary="Plan Your Financial Goals" secondary="Use the projections to plan for future milestones like education, retirement, or big purchases." />
                    </ListItem>
                </List>

                <Typography variant="h5" gutterBottom>
                    Final Thoughts
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                    Our SIP Investment Calculator simplifies the process of planning and optimizing your monthly investments. Whether
                    you're a beginner or a seasoned investor, this tool offers valuable insights to help you make the most of your
                    SIP contributions.
                </Typography>
                <Typography variant="body1" paragraph>
                    Try it now and start building your wealth, one SIP at a time!
                </Typography>
            </CustomCard>
            {/* BLOG WIDGET ENDS */}
        </div >
    );
};

export default Example;
